import { Box, Chip, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { Button } from "@/components/common/Button"
import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { AerosSelect } from "@/components/UiKit/AerosSelect"
import { PRODUCT_STATUS_CHIP_COLOR_MAP, PRODUCT_STATUS_LABEL_MAP } from "@/constants/product"
import type { Product, UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { PricingDialogs } from "@/screens/Pricing/constants"
import { createUomsList } from "@/utils/conversion"

interface PricingProductHeaderProps {
  product: Product
  uoms: UnitOfMeasurement[]
  selectedUomId: string | undefined
  onUomChange: (id: string) => void
  setDialogOpen: (open: PricingDialogs) => void
  setSelectedProduct: (product: Product) => void
}

export const PricingProductHeader: FC<PricingProductHeaderProps> = ({
  product,
  uoms,
  selectedUomId,
  onUomChange,
  setDialogOpen,
  setSelectedProduct,
}) => {
  const handleUomChange = (value: string | null) => {
    if (!value) return
    onUomChange(value)
  }

  const status = product.computedProductStatus

  const uomsDropdownList = createUomsList(uoms, product).map(({ id, name }) => ({ value: id ?? "", label: name ?? "" }))

  return (
    <Box className='flex items-center gap-10 border border-solid border-gray-400 bg-gray-100 p-6'>
      <Box className='flex items-center gap-4'>
        <Typography variant='h5' className='text-[#37474F]'>
          {product.vendorDescription}
        </Typography>
        {status && (
          <Chip
            label={PRODUCT_STATUS_LABEL_MAP[status]}
            variant='outlined'
            color={PRODUCT_STATUS_CHIP_COLOR_MAP[status]}
            size='small'
            className='rounded-full'
          />
        )}
      </Box>

      <Box className='flex items-center gap-2'>
        <Box>Product ID {product.productId}</Box>
        <Box>
          <DisplayAS400Id id={product.externalSystemId} />
        </Box>
      </Box>

      {product.company?.name && (
        <Typography variant='body2' className='text-[#455A64]'>
          Supplier: {product.company.name}
        </Typography>
      )}

      <Box className='flex items-center gap-2'>
        <Typography variant='body2' className='text-[#37474F]'>
          UoM:
        </Typography>

        <AerosSelect
          options={uomsDropdownList}
          value={selectedUomId ?? ""}
          onChange={handleUomChange}
          className='w-20'
          size='small'
        />

        <Button
          appearance='outlined'
          onClick={() => {
            setDialogOpen(PricingDialogs.AddSpecialPrice)
            setSelectedProduct(product)
          }}
          startIcon={<AddOutlined />}
        >
          Add Special Price
        </Button>
      </Box>
    </Box>
  )
}
